import React from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useColors } from '../../../hooks/useColors';

/**
 * Shared component for dashboard routes
 * @param {Object} props - Component props
 * @param {Array} props.routes - Array of route configurations
 * @param {number} props.requiredRole - Minimum role required to access these routes
 * @param {string} props.fallbackPath - Path to redirect to if user doesn't have required role
 */
const DashboardRoutes = ({ 
  routes = [], 
  requiredRole = 0, 
  fallbackPath = '/home' 
}) => {
  const { semantic } = useColors();
  const { user } = useAuth();

  // Redirect if user doesn't have required role
  if (!user || user.role < requiredRole) {
    return <Navigate to={fallbackPath} replace />;
  }

  return (
    <div className="min-h-screen">
      <Routes>
        {routes.map(({ path, element: Element, requiredRole: routeRole }) => {
          // Skip routes that require higher role than user has
          if (routeRole && user.role < routeRole) {
            return null;
          }
          
          // Create a component to render
          const RouteComponent = () => {
            const ElementComponent = Element;
            return <ElementComponent />;
          };
          
          return (
            <Route key={path} path={path} element={<RouteComponent />} />
          );
        })}
      </Routes>
    </div>
  );
};

DashboardRoutes.propTypes = {
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      element: PropTypes.elementType.isRequired,
      requiredRole: PropTypes.number
    })
  ).isRequired,
  requiredRole: PropTypes.number,
  fallbackPath: PropTypes.string
};

export default DashboardRoutes;

